import { paths } from '@/utils/path'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { Alert, Button, TextField } from 'vnetwork-ui'
import { resetPassword } from '../../services'
import LoadIcons from '@/icons/LoadIcons'
import { AxiosError } from 'axios'
import { ErrorResponse } from '@/types/shared'
import CheckIcons from '@/icons/CheckIcons'
import { useTranslation } from 'react-i18next'
import VersionInfo from '../VersionInfo'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const ForgotPassword = () => {
    const {
        register,
        formState: { errors },
        handleSubmit,
        getValues,
    } = useForm({
        defaultValues: {
            username: '',
        },
    })

    const { t } = useTranslation()

    const { mutateAsync, isPending, error, isError, isSuccess } =
        useMutationWithHandler(
            {
                mutationFn: resetPassword,
            },
            {
                reactQueryKeys: QUERY_KEYS.GENERIC,
                showSuccessToast: false,
                showErrorToast: false,
            }
        )

    const handleRetries = () => mutateAsync({ username: getValues('username') })

    const handleCheckMail = () => {
        window.open(import.meta.env.APP_MAIL_DOMAIN, '_blank')
    }

    const errorMessage =
        (error as AxiosError<ErrorResponse>)?.response?.data
            ?.error_description ||
        error?.message ||
        'Something went wrong'

    return (
        <div
            className="w-full"
            style={{
                fontFamily: 'IBM Plex Sans, Helvetica, Arial, sans-serif',
            }}
        >
            {/* Carbon Header */}
            <div className="mb-6 md:mb-8">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-light text-black leading-tight mb-2">
                    {t('forgot-password')}
                </h1>
                <p className="text-sm md:text-base text-gray-600">
                    {t('dont-worry-text')}
                </p>
            </div>

            {/* Carbon Form */}
            <div className="space-y-4 md:space-y-6">
                {isError && (
                    <Alert
                        size="medium"
                        variant="error"
                        className="mb-4 text-center"
                    >
                        {errorMessage}
                    </Alert>
                )}

                {!isSuccess ? (
                    <div className="space-y-3 md:space-y-4">
                        <label className="text-sm font-medium text-black block">
                            {t('email-address')}
                        </label>

                        <div className="border border-gray-300 hover:border-blue-600 transition-colors">
                            <div className="p-4">
                                <form
                                    onSubmit={handleSubmit((data) =>
                                        mutateAsync(data)
                                    )}
                                >
                                    <div className="space-y-3">
                                        <TextField
                                            {...register('username', {
                                                required: {
                                                    message:
                                                        t('email-required'),
                                                    value: true,
                                                },
                                                pattern: {
                                                    message:
                                                        t('wrong-email-format'),
                                                    value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                                                },
                                            })}
                                            className="w-full bg-gray-50"
                                            type="text"
                                            placeholder={t(
                                                'enter-email-placeholder'
                                            )}
                                            size="medium"
                                            error={!!errors.username}
                                            helperText={
                                                errors?.username?.message
                                            }
                                        />

                                        <Button
                                            loading={isPending}
                                            disabled={isPending}
                                            variant="contained"
                                            color="default"
                                            size="medium"
                                            className="w-full"
                                            type="submit"
                                        >
                                            {t('send')}
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Success State */}
                        <div className="bg-green-50 border border-green-200 p-4 sm:p-6">
                            <div className="flex items-start space-x-3 sm:space-x-4">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 border border-green-300 flex items-center justify-center flex-shrink-0">
                                    <div className="w-5 h-5 sm:w-6 sm:h-6 text-green-600">
                                        ✓
                                    </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-base sm:text-lg font-medium text-black mb-2">
                                        {t('email-sent-successfully')}
                                    </h3>
                                    <p className="text-sm text-gray-600 leading-relaxed mb-4">
                                        {t('reset-email-sent-message')}
                                    </p>

                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <Button
                                            color="default"
                                            variant="contained"
                                            size="medium"
                                            className="flex items-center justify-center bg-green-600 hover:bg-green-700 text-white"
                                            type="button"
                                            onClick={handleCheckMail}
                                        >
                                            <CheckIcons />
                                            <span className="ml-2">
                                                {t('check-your-mail')}
                                            </span>
                                        </Button>

                                        <Button
                                            color="default"
                                            variant="outlined"
                                            size="medium"
                                            className="flex items-center justify-center"
                                            type="button"
                                            onClick={handleRetries}
                                        >
                                            <LoadIcons className="mr-2" />
                                            {t('resend-email')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Back to Sign In */}
                <div className="text-center">
                    <Link
                        to={paths.login}
                        className="text-sm text-blue-600 hover:text-blue-700 hover:underline inline-flex items-center"
                    >
                        ← {t('back-to-sign-in')}
                    </Link>
                </div>

                {/* Terms Notice */}
                <div className="text-xs text-gray-500 leading-relaxed">
                    {t('terms-notice-prefix')}{' '}
                    <a
                        href="#"
                        className="text-blue-600 hover:text-blue-700 underline"
                    >
                        {t('terms-of-service')}
                    </a>{' '}
                    {t('and')}{' '}
                    <a
                        href="#"
                        className="text-blue-600 hover:text-blue-700 underline"
                    >
                        {t('privacy-policy')}
                    </a>
                    .
                </div>

                <VersionInfo align="left" />
            </div>
        </div>
    )
}

export default ForgotPassword
